import React, { forwardRef, useEffect, useMemo } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import useRoles from 'views/App/Portal/LexID/Roles/_data_sources/useRoles';
import { Role } from 'views/App/Portal/LexID/Roles/_types/role';

interface RoleOption {
  value: string;
  label: string;
  role: Role;
}

export type RolePickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  organizationId: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
};

const RolePicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      organizationId,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
    }: RolePickerProps,
    ref
  ) => {
    const rolesQuery = useRoles(organizationId, { size: 100 });

    const roleOptions = useMemo(() => {
      try {
        const allRoles = rolesQuery.data?.pages?.flatMap((page) => page.content) ?? [];
        return [
          ...(allowBlank ? [{ value: '', label: placeholder ?? '-- All Roles --' }] : []),
          ...allRoles.map((x) => ({
            value: x.id!,
            label: `${x.name ?? ''}`,
            role: x,
          })),
        ];
      } catch (e) {
        return [];
      }
    }, [rolesQuery.data, allowBlank]);

    const selectedOptions = useMemo(() => {
      return roleOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, roleOptions]);

    useEffect(() => {
      if (rolesQuery.hasNextPage) {
        rolesQuery.fetchNextPage();
      }
    }, [rolesQuery.data, rolesQuery.hasNextPage]);

    return rolesQuery.data?.pages[0]?.content ? (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{
          p: 0,
          paddingTop: !!label ? '1rem' : 0,
        }}
      >
        {!!label && (
          <InputLabel
            required={required}
            sx={{
              fontSize: '0.95rem',
              shrink: {
                transform: 'translate(0px, -8px) scale(1) !important',
                backgroundColor: 'transparent',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={roleOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<RoleOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<RoleOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    ) : (
      <CircularProgress />
    );
  }
);

export default RolePicker;
